import React from 'react';

import { useNavigate } from 'react-router-dom';

import { CheckCircle, ArrowLeft, Loader2, AlertTriangle, Users, Clock, Save } from 'lucide-react';

import { Button } from '../../components/ui/button';

import { Progress } from '../../components/ui/progress';

import { cn } from '../../lib/utils';

import BackButton from '../../components/BackButton';

import { WORKFLOW_STEPS } from './ftpeConstants';

import { useFtpeWorkflow } from './useFtpeWorkflow';
import FtpeSteps123 from './FtpeSteps123';
import FtpeSteps456 from './FtpeSteps456';

export default function AdhesionFTPEWorkflow() {
  const ctx = useFtpeWorkflow();
  const navigate = useNavigate();
  const goBack = () => {
    const idx = window.history.state?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate('/adherer-ftpe');
  };
  const { autoSaveEnabled, currentStep, error, estimatedTime, forMemberMode, lastSaved, memberEntrepriseFromUrl, memberNameFromUrl, otpSent, progress, progressPercentage, savingDraft, setAutoSaveEnabled, setCurrentStep, setError, setOtpCode, setOtpSent, text: text } = ctx;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header avec logo */}
      <div className="bg-black text-white py-8 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Bouton Retour */}
          <div className="mb-4">
            <BackButton variant="dark" label="Retour" homeLabel="Espace FTPE Outremer" />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Adhésion FTPE Outremer</h1>
              <p className="text-gray-400 mt-1">Fédération des Très Petites Entreprises Outremer</p>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_80d9c916-7281-4428-9c84-a6e0cd256c92/artifacts/ojac2aim_logo%20ftpe%20outremer%20dans%20fond%20blanc.png"
              alt="FTPE Outremer"
              className="h-16 object-contain rounded-lg"
            />
          </div>
          
          {/* Indicateur de progression amélioré */}
          {currentStep < 6 && (
            <div className="mt-6 p-4 bg-white/10 rounded-lg backdrop-blur-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-amber-400 font-semibold text-lg">{progressPercentage}%</span>
                  <span className="text-gray-300">complété</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="w-4 h-4" />
                  <span>~{estimatedTime} min restantes</span>
                </div>
              </div>
              <Progress value={progressPercentage} className="h-2 bg-white/20" />
              
              {/* Auto-save indicator */}
              {currentStep === 3 && (
                <div className="flex items-center justify-between mt-3 text-xs">
                  <div className="flex items-center gap-2">
                    {savingDraft ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin text-amber-400" />
                        <span className="text-amber-400">Sauvegarde en cours...</span>
                      </>
                    ) : lastSaved ? (
                      <>
                        <CheckCircle className="w-3 h-3 text-green-400" />
                        <span className="text-green-400">
                          Sauvegardé à {lastSaved.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </>
                    ) : (
                      <>
                        <Save className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-400">Auto-sauvegarde activée (toutes les 30s)</span>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
                    className={cn(
                      "text-xs px-2 py-1 rounded",
                      autoSaveEnabled ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
                    )}
                  >
                    {autoSaveEnabled ? 'Auto-save ON' : 'Auto-save OFF'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bandeau Mode "Pour un membre" */}
      {forMemberMode && memberNameFromUrl && (
        <div className="bg-blue-600 text-white py-3 px-6">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <Users className="h-5 w-5" />
            <div>
              <span className="font-semibold">Mode Adhésion pour un membre</span>
              <span className="mx-2">•</span>
              <span>{decodeURIComponent(memberNameFromUrl)}</span>
              {memberEntrepriseFromUrl && (
                <>
                  <span className="mx-2">-</span>
                  <span className="text-blue-100">{decodeURIComponent(memberEntrepriseFromUrl)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="bg-white border-b shadow-sm py-4 px-6 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            {WORKFLOW_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={cn(
                    "flex flex-col items-center",
                    isCurrent && "text-amber-600",
                    isCompleted && "text-green-600",
                    !isCurrent && !isCompleted && "text-gray-400"
                  )}>
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all",
                      isCurrent && "border-amber-600 bg-amber-50",
                      isCompleted && "border-green-600 bg-green-600 text-white",
                      !isCurrent && !isCompleted && "border-gray-300"
                    )}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <span className="text-xs mt-1 font-medium hidden sm:block">{step.name}</span>
                  </div>
                  {index < WORKFLOW_STEPS.length - 1 && (
                    <div className={cn(
                      "w-12 sm:w-20 h-0.5 mx-2",
                      isCompleted ? "bg-green-600" : "bg-gray-200"
                    )} />
                  )}
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-1" />
          
          {/* Bouton retour vers la page précédente — toujours visible */}
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              data-testid="ftpe-workflow-page-back-btn"
              onClick={goBack}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
            {currentStep > 1 && currentStep < 6 && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="workflow-back-btn"
                onClick={() => {
                  if (currentStep === 4 && otpSent) {
                    setOtpSent(false);
                    setOtpCode('');
                  }
                  setCurrentStep(currentStep - 1);
                  setError('');
                }}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Etape precedente
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError('')} className="ml-auto">
              ✕
            </Button>
          </div>
        )}

        <FtpeSteps123 ctx={ctx} />
        <FtpeSteps456 ctx={ctx} />
      </div>
    </div>
  );
}
